import { useEffect, useMemo, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';

import MarketplaceEnquiryModal from '@/components/MarketplaceEnquiryModal';
import SEO from '@/components/SEO';

const BACKEND_URL =
  process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const formatPrice = (value) =>
  new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    maximumFractionDigits: 0,
  }).format(Number(value || 0));

const typeLabels = {
  physical: 'Original / Handmade',
  digital: 'Digital asset',
  service: 'Creative service',
};

const normalizeProduct = (product = {}) => ({
  ...product,
  id: product.id || product._id || '',
  title: product.title || 'KalaManch product',
  description: product.description || '',
  category: product.category || 'Art',
  subcategory: product.subcategory || '',
  product_type: product.product_type || 'physical',
  price_inr: Number(product.sale_price_inr ?? product.price_inr ?? 0),
  image_urls: Array.isArray(product.image_urls) ? product.image_urls : [],
});

export default function MarketplaceProductDetail() {
  const { productId } = useParams();
  const [product, setProduct] = useState(null);
  const [activeImage, setActiveImage] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchParams] = useSearchParams();
  const [showEnquiry, setShowEnquiry] = useState(searchParams.get('checkout') === '1');

  useEffect(() => {
    let mounted = true;
    const loadProduct = async () => {
      setLoading(true);
      try {
        const response = await axios.get(
          `${API}/marketplace/products/${encodeURIComponent(productId)}`
        );
        const nextProduct = normalizeProduct(response.data?.product || {});
        if (mounted) {
          setProduct(nextProduct);
          setActiveImage(nextProduct.image_urls?.[0] || '/images/kalamanch-logo.png');
          setError('');
        }
      } catch (err) {
        if (mounted) {
          setProduct(null);
          setError(err?.response?.data?.detail || 'Marketplace product could not be loaded.');
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    loadProduct();
    return () => {
      mounted = false;
    };
  }, [productId]);

  const detailRows = useMemo(() => {
    if (!product) return [];
    return [
      ['Category', product.category],
      ['Subcategory', product.subcategory],
      ['Medium', product.medium],
      ['Dimensions', product.dimensions],
      ['Ships from', product.shipping_from || product.artist_city],
      ['Type', typeLabels[product.product_type] || product.product_type],
      ['Available quantity', product.inventory_count ? String(product.inventory_count) : 'Confirm with KalaManch'],
    ].filter(([, value]) => Boolean(value));
  }, [product]);

  if (loading) {
    return (
      <main className="min-h-screen bg-[#f7f9fc] px-4 pt-32">
        <div className="mx-auto max-w-5xl rounded-[1.5rem] border border-slate-200 bg-white p-8 text-center shadow-lg">
          <p className="text-sm font-black uppercase tracking-[0.2em] text-[#4A148C]">
            Loading product
          </p>
        </div>
      </main>
    );
  }

  if (error || !product) {
    return (
      <main className="min-h-screen bg-[#f7f9fc] px-4 pt-32">
      <SEO title="Marketplace product not found | KalaManch Market" />
        <div className="mx-auto max-w-4xl rounded-[1.5rem] border border-slate-200 bg-white p-8 text-center shadow-lg">
          <p className="text-sm font-black uppercase tracking-[0.2em] text-[#4A148C]">
            KalaManch Market
          </p>
          <h1 className="mt-3 text-3xl font-black text-slate-950">
            Product not found
          </h1>
          <p className="mt-3 text-slate-600">{error}</p>
          <Link
            to="/marketplace"
            className="mt-6 inline-flex rounded-full bg-[#FF6F00] px-6 py-3 text-sm font-black text-white"
          >
            Back to Marketplace
          </Link>
        </div>
      </main>
    );
  }

  const artistName = 'KalaManch';
  const artistImage = '/images/kalamanch-logo.png';

  return (
    <main className="min-h-screen bg-[#f7f9fc] pt-28">
      <SEO
        title={`${product.title} | KalaManch Market`}
        description={
          product.description ||
          `Request availability for ${product.title} by ${artistName} on KalaManch Market.`
        }
        keywords={`${product.title}, ${product.category}, ${product.subcategory}, KalaManch Market`}
        image={product.image_urls?.[0]}
      />

      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6 lg:px-8">
        <Link
          to="/marketplace"
          className="inline-flex rounded-full border border-orange-200 bg-white px-5 py-2 text-sm font-black text-[#C45A00] shadow-sm"
        >
          Back to Marketplace
        </Link>

        <div className="mt-5 grid gap-8 lg:grid-cols-[1.04fr_0.96fr]">
          <div className="rounded-[1.6rem] border border-slate-200 bg-white p-4 shadow-xl">
            <div className="aspect-[4/3] overflow-hidden rounded-[1.2rem] bg-slate-100">
              <img
                src={activeImage || '/images/kalamanch-logo.png'}
                alt={product.title}
                className="h-full w-full object-contain"
              />
            </div>
            {product.image_urls.length > 1 && (
              <div className="mt-4 grid grid-cols-4 gap-3 sm:grid-cols-6">
                {product.image_urls.map((imageUrl) => (
                  <button
                    type="button"
                    key={imageUrl}
                    onClick={() => setActiveImage(imageUrl)}
                    className={`aspect-square overflow-hidden rounded-xl border bg-slate-100 ${
                      activeImage === imageUrl ? 'border-[#FF6F00]' : 'border-slate-200'
                    }`}
                  >
                    <img
                      src={imageUrl}
                      alt=""
                      className="h-full w-full object-cover"
                      loading="lazy"
                    />
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-[1.6rem] border border-orange-100 bg-white p-6 shadow-xl sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#4A148C]">
              KalaManch Market
            </p>
            <h1 className="mt-3 text-4xl font-black leading-tight text-slate-950">
              {product.title}
            </h1>
            <p className="mt-4 text-3xl font-black text-[#C45A00]">
              {formatPrice(product.price_inr)}
            </p>
            <div className="mt-5 flex flex-wrap gap-2">
              <span className="rounded-full bg-orange-50 px-3 py-1 text-xs font-black uppercase tracking-[0.12em] text-[#C45A00]">
                {typeLabels[product.product_type] || product.product_type}
              </span>
              <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-black text-emerald-700">
                Enquire first, confirm later
              </span>
            </div>
            <p className="mt-5 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm font-bold leading-6 text-[#9A4300]">
              This is a request/reservation flow. KalaManch confirms availability, payment method and delivery before the order is final.
            </p>
            <button
              type="button"
              onClick={() => setShowEnquiry(true)}
              className="mt-6 w-full rounded-full bg-[#FF6F00] px-6 py-4 text-base font-black text-white shadow-lg transition hover:-translate-y-0.5 hover:bg-[#E65F00]"
            >
              Request availability
            </button>

            <div className="mt-7 rounded-[1.2rem] border border-slate-200 bg-slate-50 p-4">
              <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-500">
                Sold and managed by
              </p>
              <div className="mt-3 flex items-center gap-3">
                <img
                  src={artistImage}
                  alt={artistName}
                  className="h-14 w-14 rounded-full border border-orange-100 object-cover"
                />
                <div>
                  <p className="font-black text-slate-950">{artistName}</p>
                  <p className="text-sm font-bold text-slate-500">
                    Products and services from KalaManch
                  </p>
                </div>
              </div>

            </div>
          </div>
        </div>

        <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_22rem]">
          <section className="rounded-[1.6rem] border border-slate-200 bg-white p-6 shadow-lg sm:p-8">
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#4A148C]">
              Product details
            </p>
            <h2 className="mt-3 text-3xl font-black text-slate-950">Description</h2>
            <p className="mt-4 whitespace-pre-line text-base leading-8 text-slate-700">
              {product.description || 'KalaManch admin will confirm full details for this listing.'}
            </p>
          </section>

          <aside className="rounded-[1.6rem] border border-slate-200 bg-white p-6 shadow-lg">
            <p className="text-xs font-black uppercase tracking-[0.24em] text-[#4A148C]">
              Quick facts
            </p>
            <div className="mt-4 grid gap-3">
              {detailRows.map(([label, value]) => (
                <div
                  key={label}
                  className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                >
                  <p className="text-xs font-black uppercase tracking-[0.14em] text-slate-500">
                    {label}
                  </p>
                  <p className="mt-1 font-bold text-slate-900">{value}</p>
                </div>
              ))}
            </div>
          </aside>
        </div>
      </section>

      {showEnquiry && (
        <MarketplaceEnquiryModal product={product} onClose={() => setShowEnquiry(false)} />
      )}
    </main>
  );
}
